import React, {useState, useEffect, useMemo} from 'react';
import {Box, Stack, styled} from '@mui/material';
import {
  Button,
  designSystemToken,
  Headline,
  Icon,
} from '@lightricks/react-design-system';
import {CaptionsStylePreset, Animation} from '@contentlab/types/captions';
import translate from '@/utils/translate';
import {scrollbarMixins} from '@/styles/mixins';
import AccordionSection from './AccordionSection';
import AnimationSection from './AnimationSection';
import PositionSection from './PositionSection';
import StylingSection from './StylingSection';
import TypographySection from './TypographySection';

const Container = styled(Box)(() => ({
  width: '100%',
  height: '100%',
  padding: '0px',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: designSystemToken('semantic.bg.primary'),
}));

const PageHeader = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  marginBottom: '24px',
  marginTop: '24px',
  marginLeft: '24px',
  marginRight: '24px',
}));

const HeaderLeftPanel = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
}));

const ResetButton = styled(Button)(() => ({
  'minWidth': 'auto',
  'padding': '6px 16px',
  'borderRadius': '40px',
  'backgroundColor': 'transparent',
  'border': 'none',
  'fontSize': '14px',
  'fontWeight': 500,
  'lineHeight': '15px',
  'letterSpacing': '-0.5px',
  '&:hover': {
    backgroundColor: designSystemToken('semantic.bg.secondary'),
  },
}));

const StyledBackButton = styled(Button)(() => ({
  'minWidth': 'auto',
  'padding': '0px',
  'backgroundColor': 'transparent',
  'border': 'none',
  '&:hover': {
    backgroundColor: designSystemToken('semantic.bg.secondary'),
  },
}));

interface StyleEditorProps {
  style: CaptionsStylePreset;
  onStyleChange: (style: CaptionsStylePreset) => void;
  onBack: () => void;
  onSave: (style: CaptionsStylePreset) => void;
  onReset?: (style: CaptionsStylePreset) => void;
}

const areStylesEqual = (
  style1: CaptionsStylePreset,
  style2: CaptionsStylePreset
): boolean => {
  return JSON.stringify(style1) === JSON.stringify(style2);
};

function StyleEditor({
  style,
  onStyleChange,
  onBack,
  onSave,
  onReset,
}: StyleEditorProps) {
  const [originalStyle, setOriginalStyle] = useState<CaptionsStylePreset>(() =>
    structuredClone(style)
  );
  const [editedStyle, setEditedStyle] = useState<CaptionsStylePreset>(() =>
    structuredClone(style)
  );
  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({
    typography: true,
    styling: false,
    position: false,
    animation: false,
  });

  useEffect(() => {
    if (style.id !== originalStyle.id) {
      const newOriginal = structuredClone(style);
      setOriginalStyle(newOriginal);
      setEditedStyle(structuredClone(style));
    }
  }, [style, originalStyle.id]);

  const hasChanges = useMemo(() => {
    return !areStylesEqual(originalStyle, editedStyle);
  }, [originalStyle, editedStyle]);

  const updateTypography = (
    updates: Partial<typeof editedStyle.captionsStyle.typography>
  ) => {
    const newStyle = {
      ...editedStyle,
      captionsStyle: {
        ...editedStyle.captionsStyle,
        typography: {
          ...editedStyle.captionsStyle.typography,
          ...updates,
        },
      },
    };
    setEditedStyle(newStyle);
    onStyleChange(newStyle);
    onSave(newStyle);
  };

  const updateStyling = (
    updates: Partial<typeof editedStyle.captionsStyle.styling>
  ) => {
    const newStyle = {
      ...editedStyle,
      captionsStyle: {
        ...editedStyle.captionsStyle,
        styling: {
          ...editedStyle.captionsStyle.styling,
          ...updates,
        },
      },
    };
    setEditedStyle(newStyle);
    onStyleChange(newStyle);
    onSave(newStyle);
  };

  const updateAnimation = (animation: Animation | null) => {
    const newStyle = {
      ...editedStyle,
      captionsStyle: {
        ...editedStyle.captionsStyle,
        animation,
      },
    };
    setEditedStyle(newStyle);
    onStyleChange(newStyle);
    onSave(newStyle);
  };

  const updateNormalizedVerticalPosition = (
    normalizedVerticalPosition: number
  ) => {
    const newStyle = {
      ...editedStyle,
      captionsStyle: {
        ...editedStyle.captionsStyle,
        normalizedVerticalPosition,
      },
    };
    setEditedStyle(newStyle);
    onStyleChange(newStyle);
    onSave(newStyle);
  };

  const handleSectionChange = (section: string, expanded: boolean) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: expanded,
    }));
  };

  const handleReset = () => {
    if (onReset) {
      onReset(originalStyle);
    }
  };

  return (
    <Container>
      <PageHeader>
        <HeaderLeftPanel onClick={onBack}>
          <StyledBackButton appearance="neutral" mode="plain" size="large">
            <Icon
              name="Actions-Arrow-Back-Small"
              size="medium"
              appearance="neutral"
              color={designSystemToken('action.active')}
            />
          </StyledBackButton>
          <Headline size="sm">
            {translate('Edit style', {styleName: style.name})}
          </Headline>
        </HeaderLeftPanel>

        {onReset && (
          <ResetButton
            appearance="neutral"
            mode="plain"
            size="medium"
            onClick={handleReset}
            disabled={!hasChanges}
          >
            <Icon
              name="Actions-Reset"
              size="medium"
              appearance="neutral"
              color={designSystemToken(
                hasChanges ? 'semantic.fg.primary' : 'semantic.fg.disabled'
              )}
            />
            {translate('Reset')}
          </ResetButton>
        )}
      </PageHeader>

      <Stack
        gap="12px"
        sx={{
          flex: 1,
          overflow: 'auto',
          padding: '0px',
          ...scrollbarMixins.overrideScrollbar(),
        }}
      >
        <AccordionSection
          sectionKey="typography"
          title="Typography"
          iconName="Features-Text"
          expanded={expandedSections.typography}
          onToggle={handleSectionChange}
        >
          <TypographySection
            updateTypography={updateTypography}
            editedStyle={editedStyle}
          />
        </AccordionSection>

        <AccordionSection
          sectionKey="styling"
          title="Styling"
          iconName="Features-Design"
          expanded={expandedSections.styling}
          onToggle={handleSectionChange}
        >
          <StylingSection
            updateStyling={updateStyling}
            editedStyle={editedStyle}
          />
        </AccordionSection>

        <AccordionSection
          sectionKey="position"
          title="Position"
          iconName="Actions-Move"
          expanded={expandedSections.position}
          onToggle={handleSectionChange}
        >
          <PositionSection
            updateNormalizedVerticalPosition={updateNormalizedVerticalPosition}
            editedStyle={editedStyle}
          />
        </AccordionSection>

        <AccordionSection
          sectionKey="animation"
          title="Animation"
          iconName="Features-Animation-Main"
          expanded={expandedSections.animation}
          onToggle={handleSectionChange}
        >
          <AnimationSection
            updateAnimation={updateAnimation}
            editedStyle={editedStyle}
          />
        </AccordionSection>
      </Stack>
    </Container>
  );
}

export default StyleEditor;
